import { ArrowUpRightIcon, Rocket02Icon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import EmptyState from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { HostListenersTable } from '@/components/HostListenersTable';
import {
  HOST_LISTENERS_PAGE_DESCRIPTION,
  HOST_LISTENERS_PAGE_TITLE,
} from '@/constants/pageTitlesAndDescriptionConstants';
import { Button } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrganizationId } from '@/hooks/useSwr';
import { appOrganizationPath } from '@/utils/constants';

import { HostListenerCrudPaneWithButton } from '../../components/HostListenerCrudPane/HostListenerCrudPaneWithButton';
import { PageTitle } from '../../components/PageTitle';

const HostListenersBody = () => {
  const { authorize } = usePermissions();
  const [createListenerOpen, setCreateListenerOpen] = useState(false);

  return (
    <>
      <div className="my-4 flex items-center justify-between gap-4">
        <PageTitle
          className="py-0"
          description={HOST_LISTENERS_PAGE_DESCRIPTION}
        >
          {HOST_LISTENERS_PAGE_TITLE}
        </PageTitle>
        <div className="flex gap-2">
          {authorize('deployments:create') && (
            <HostListenerCrudPaneWithButton
              open={createListenerOpen}
              setIsOpen={setCreateListenerOpen}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <HostListenersTable />
      </div>
    </>
  );
};

const HostListeners = () => {
  const { value: canUseLangGraphCloud, isLoading: isLangGraphCloudLoading } =
    useOrgConfig(OrgConfigs.can_use_langgraph_cloud);
  const { value: enableLgpListenersPage } = useOrgConfig(
    OrgConfigs.enable_lgp_listeners_page
  );
  const organizationId = useOrganizationId();

  const { authorize, isLoading: isPermsLoading } = usePermissions();

  if (isLangGraphCloudLoading) {
    return <LinearProgress />;
  }

  if (!canUseLangGraphCloud) {
    return (
      <div className="h-[100vh] px-4 pb-6 pt-3">
        <Breadcrumbs />

        <div className="my-4 flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <PageTitle
              className="py-0"
              description={HOST_LISTENERS_PAGE_DESCRIPTION}
            >
              {HOST_LISTENERS_PAGE_TITLE}
            </PageTitle>
          </div>
        </div>

        <div className="flex h-[70vh] flex-col items-center justify-center gap-2">
          <EmptyState
            title="LangGraph Platform is available to users on a paid plan."
            description={'Upgrade to access'}
            icon={<Rocket02Icon />}
            fancyIcon
            action={
              <RouterLink
                to={`/${appOrganizationPath}/${organizationId}/settings/payments`}
              >
                <Button variant="outlined" rightDecorator={ArrowUpRightIcon}>
                  Upgrade
                </Button>
              </RouterLink>
            }
          />
        </div>
      </div>
    );
  }

  if (isPermsLoading) {
    return <LinearProgress />;
  }

  if (!authorize('deployments:read') || !enableLgpListenersPage) {
    return <ErrorBanner className="m-4">Forbidden</ErrorBanner>;
  }

  return (
    <div className="px-4 pb-6 pt-3">
      <Breadcrumbs />

      <HostListenersBody />
    </div>
  );
};

export default HostListeners;
